import type { VoidComponent } from "solid-js";
import { createMemo, Show } from "solid-js";
import InputModal from "../../../components/modal/InputModal";
import { useTranslation } from "../../../state/i18n";
import { isNil } from "lodash-es";
import { object } from "valibot";
import UserService from "../../../services/UserService";
import { useCustomerState } from "../../../state/customer";
import { toSelectOption } from "../../../utils/avatarUtil";
import { userReference } from "../../../utils/validation/idReferences";
import type UserId from "../../../state/UserId";
import { SingleSelectWithSearchInput } from "../../../components/menu/SingleSelectWithSearch";
import GenericFailureModal from "../../GenericFailureModal";
import { useModalManager } from "../../../components/modal/ModalManager";

export const transferOwnershipSchema = () =>
  object({
    user: userReference(),
  });

const TransferOwnershipModal: VoidComponent = () => {
  const { t } = useTranslation();
  const { replace } = useModalManager();
  const customer = useCustomerState();
  const users = UserService.getUsers(customer.customerId);
  const accountOwner = UserService.getAccountOwner(customer.customerId);

  const userData = createMemo(() =>
    !isNil(users.data) && !isNil(accountOwner.data)
      ? {
          users: users.data,
          accountOwner: accountOwner.data,
        }
      : undefined,
  );

  return (
    <InputModal
      variant="default"
      size="large"
      title={t(($) => $.TRANSFER_OWNERSHIP_MODAL_TITLE)}
      confirmLabel={t(($) => $.TRANSFER_OWNERSHIP_MODAL_SAVE_CHANGES)}
      cancelLabel={t(($) => $.CANCEL_BUTTON)}
      onConfirm={(value) =>
        new Promise((resolve, reject) => {
          UserService.transferOwnership(customer.customerId).mutate(value, {
            onSuccess: resolve,
            onError: (err) => {
              replace({
                content: () => (
                  <GenericFailureModal title={t(($) => $.TRANSFER_OWNERSHIP_FAILURE_MODAL_TITLE)} error={err} />
                ),
              });
              reject(err);
            },
          });
        })
      }
      description={t(($) => $.TRANSFER_OWNERSHIP_MODAL_DESCRIPTION)}
      schema={transferOwnershipSchema()}
      initialValue={{ user: undefined }}
    >
      {({ value, updateValue, error }) => (
        <Show when={userData()}>
          {(data) => (
            <SingleSelectWithSearchInput<UserId>
              label={t(($) => $.TRANSFER_OWNERSHIP_MODAL_SELECT_NEW_ACCOUNT_OWNER)}
              placeholder={t(($) => $.TRANSFER_OWNERSHIP_MODAL_SELECT_USER)}
              value={value("user")}
              required
              options={{
                users: data()
                  .users.filter((user) => user.emailAddress !== data().accountOwner.emailAddress)
                  .map((user) => {
                    const option = toSelectOption(user);
                    return { ...option, value: option.value.id };
                  }),
              }}
              onSelect={(value) => updateValue("user", value!)}
              width="matchTrigger"
              errorMessage={error("user")}
            />
          )}
        </Show>
      )}
    </InputModal>
  );
};

export default TransferOwnershipModal;
